/**
 * StudioContext - React Context Provider for Studio State
 * 
 * This context makes the centralized studio state available
 * to all child components without prop drilling.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useStudioState, StudioState } from '../hooks/useStudioState';

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const StudioContext = createContext<StudioState | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface StudioProviderProps {
    children: ReactNode;
}

export const StudioProvider: React.FC<StudioProviderProps> = ({ children }) => {
    const studioState = useStudioState();

    return (
        <StudioContext.Provider value={studioState}>
            {children}
        </StudioContext.Provider>
    );
};

// ============================================================================
// HOOK FOR CONSUMING CONTEXT
// ============================================================================

/**
 * useStudioContext - Access the studio state from any child component
 * 
 * @throws Error if used outside of StudioProvider
 * 
 * @example
 * ```tsx
 * const { appState, setAppState, promptSequence } = useStudioContext();
 * ```
 */
export const useStudioContext = (): StudioState => {
    const context = useContext(StudioContext);

    if (context === undefined) {
        throw new Error('useStudioContext must be used within a StudioProvider');
    }

    return context;
};

// ============================================================================
// SELECTIVE HOOKS (for performance optimization)
// ============================================================================

/**
 * useAppState - Access only app state (reduces re-renders)
 */
export const useAppStateContext = () => {
    const { appState, setAppState, currentStage, setCurrentStage, activeTab, setActiveTab } = useStudioContext();
    return { appState, setAppState, currentStage, setCurrentStage, activeTab, setActiveTab };
};

/**
 * useSequenceContext - Access only sequence-related state
 */
export const useSequenceContext = () => {
    const {
        promptSequence, setPromptSequence,
        activePromptIndex, setActivePromptIndex,
        sequenceProgress, setSequenceProgress,
        sequenceVideoData, setSequenceVideoData,
        sortedSequenceHistory,
        sequenceMode, setSequenceMode,
    } = useStudioContext();

    return {
        promptSequence, setPromptSequence,
        activePromptIndex, setActivePromptIndex,
        sequenceProgress, setSequenceProgress,
        sequenceVideoData, setSequenceVideoData,
        sortedSequenceHistory,
        sequenceMode, setSequenceMode,
    };
};

/**
 * useDogmaContext - Access only dogma-related state
 */
export const useDogmaContext = () => {
    const {
        dogmas, setDogmas,
        activeDogmaId, setActiveDogmaId,
        activeDogma,
        isDogmaManagerOpen, setIsDogmaManagerOpen,
    } = useStudioContext();

    return {
        dogmas, setDogmas,
        activeDogmaId, setActiveDogmaId,
        activeDogma,
        isDogmaManagerOpen, setIsDogmaManagerOpen,
    };
};

/**
 * useModalContext - Access only modal visibility state
 */
export const useModalContext = () => {
    const {
        showStudioConfig, setShowStudioConfig,
        isExportDialogOpen, setIsExportDialogOpen,
        showThemeModal, setShowThemeModal,
        showApiKeyDialog, setShowApiKeyDialog,
        isShotLibraryOpen, setIsShotLibraryOpen,
        isProjectManagerOpen, setIsProjectManagerOpen,
        isCharacterManagerOpen, setIsCharacterManagerOpen,
        isDogmaManagerOpen, setIsDogmaManagerOpen,
        isContactSheetOpen, setIsContactSheetOpen,
    } = useStudioContext();

    return {
        showStudioConfig, setShowStudioConfig,
        isExportDialogOpen, setIsExportDialogOpen,
        showThemeModal, setShowThemeModal,
        showApiKeyDialog, setShowApiKeyDialog,
        isShotLibraryOpen, setIsShotLibraryOpen,
        isProjectManagerOpen, setIsProjectManagerOpen,
        isCharacterManagerOpen, setIsCharacterManagerOpen,
        isDogmaManagerOpen, setIsDogmaManagerOpen,
        isContactSheetOpen, setIsContactSheetOpen,
    };
};

export default StudioContext;
